<template>
    <v-card max-width="700" min-width="500">
        <v-card-title>
            <v-row>
                <v-col class="col-item" cols="5">
                    <v-text-field
                        :value="activeUser.first_name"
                        dense
                        hide-details
                        outlined
                        @input="updateUserField($event, 'first_name')"
                    />
                </v-col>
                <v-col class="col-item" cols="5">
                    <v-text-field
                        :value="activeUser.last_name"
                        dense
                        hide-details
                        outlined
                        @input="updateUserField($event, 'last_name')"
                    />
                </v-col>
            </v-row>
        </v-card-title>

        <v-card-text>
            <v-row>
                <v-col class="subtitle-1" cols="4">{{ "Email" | t }}:</v-col>
                <v-col class="subtitle-1">{{ activeUser.email ? activeUser.email : "-" }}</v-col>
            </v-row>
            <v-row>
                <v-col class="subtitle-1" cols="4">{{ "Phone number" | t }}:</v-col>
                <v-col cols="6">
                    <v-text-field
                        :value="activeUser.phone_number"
                        dense
                        hide-details
                        outlined
                        @input="updateUserField($event, 'phone_number')"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col class="subtitle-1" cols="4">{{ "Locale" | t }}:</v-col>
                <v-col class="col-item" cols="6">
                    <v-text-field
                        :value="activeUser.locale"
                        dense
                        autocomplete="nope"
                        hide-details
                        outlined
                        @input="updateUserField($event, 'locale')"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    v-if="!changePasswordToggle"
                    class="subtitle-1"
                    cols="4"
                    style="cursor: pointer; text-decoration: underline"
                    @click="changePasswordToggle = !changePasswordToggle"
                    >Change password</v-col
                >
                <v-col v-if="changePasswordToggle" class="subtitle-1" cols="4">{{ "New password" | t }}:</v-col>
                <v-col v-if="changePasswordToggle" class="col-item" cols="6">
                    <v-text-field
                        :rules="[rules.password]"
                        :type="passwordInputType"
                        dense
                        autocomplete="new-password"
                        outlined
                        @input="updateUserField($event, 'password')"
                    />
                </v-col>
                <v-col
                    v-if="changePasswordToggle"
                    cols="1"
                    style="cursor: pointer;"
                    @click="passwordInputType = passwordInputType !== 'password' ? 'password' : 'text'"
                    ><v-icon>mdi-eye</v-icon></v-col
                >
                <v-spacer />
                <v-col v-if="changePasswordToggle" cols="1" style="cursor: pointer;" @click="handleClosePasswordClick"
                    ><v-icon>close</v-icon></v-col
                >
            </v-row>
        </v-card-text>

        <v-divider class="mx-4"></v-divider>

        <v-card-title>{{ activeCompany.name ? activeCompany.name : "" }}</v-card-title>
        <v-card-text v-if="activeCompany && activeUser.company_role && activeUser.company_role === 'owner'">
            <div class="subtitle-1">
                {{ "Legal type" | t }}: {{ activeCompany.legal_type ? t(activeCompany.legal_type) : "-" }}
            </div>
            <div class="subtitle-1">
                {{ "Identification code" | t }}:
                {{ activeCompany.identification_code ? activeCompany.identification_code : "-" }}
            </div>
            <v-row>
                <v-col class="subtitle-1" cols="4">{{ "EU vat number" | t }}:</v-col>
                <v-col class="col-item" cols="6">
                    <v-text-field
                        :value="activeCompany.eu_vat_number"
                        dense
                        hide-details
                        outlined
                        @input="updateCompanyField($event, 'eu_vat_number')"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col class="subtitle-1" cols="4">{{ "Country" | t }}:</v-col>
                <v-col class="col-item" cols="6">
                    <v-select
                        :value="activeCompany.country"
                        :items="['EE', 'LV', 'LT']"
                        dense
                        hide-details
                        outlined
                        @input="updateCompanyField($event, 'country')"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col class="subtitle-1" cols="4">{{ "Homepage URL" | t }}: </v-col>
                <v-col class="col-item" cols="6">
                    <v-text-field
                        :value="activeCompany.client_url"
                        dense
                        hide-details
                        outlined
                        @input="updateCompanyField($event, 'client_url')"
                    />
                </v-col>
                <v-col cols="1">
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon color="grey" dark v-bind="attrs" v-on="on">
                                mdi-information
                            </v-icon>
                        </template>
                        <span>{{ "Your company website URL" | t }}</span>
                    </v-tooltip>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="subtitle-1" cols="4">{{ "Postback URL" | t }}:</v-col>
                <v-col class="col-item" cols="6">
                    <v-text-field
                        :value="activeCompany.postback_url"
                        dense
                        hide-details
                        outlined
                        @input="updateCompanyField($event, 'postback_url')"
                    />
                </v-col>
                <v-col cols="1">
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon color="grey" dark v-bind="attrs" v-on="on">
                                mdi-information
                            </v-icon>
                        </template>
                        <span>{{
                            "After a successful payment the system will send POST request against entered URL" | t
                        }}</span>
                    </v-tooltip>
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-text v-else>
            <div v-if="activeUser.company_role && activeUser.company_role === 'owner'" class="subtitle-1">
                {{ "Role" | t }}: {{ activeUser.company_role ? t(activeUser.company_role) : "-" }}
            </div>
            <div class="subtitle-1">
                {{ "Legal type" | t }}: {{ activeCompany.legal_type ? t(activeCompany.legal_type) : "-" }}
            </div>
            <div class="subtitle-1">
                {{ "Identification code" | t }}:
                {{ activeCompany.identification_code ? activeCompany.identification_code : "-" }}
            </div>
            <div class="subtitle-1">
                {{ "EU vat number" | t }}:
                {{ activeCompany.eu_vat_number ? activeCompany.eu_vat_number : "-" }}
            </div>
            <div class="subtitle-1">{{ "Country" | t }}: {{ activeCompany.country ? activeCompany.country : "-" }}</div>
            <div class="subtitle-1">
                {{ "Homepage URL" | t }}: {{ activeCompany.client_url ? activeCompany.client_url : "-" }}
            </div>
        </v-card-text>

        <v-divider class="mx-4"></v-divider>
        <v-card-title>{{ "Company contact" | t }}</v-card-title>
        <v-card-text v-if="activeUser.company_role && activeUser.company_role === 'owner'">
            <v-row>
                <v-col class="subtitle-1" cols="4">{{ "Email" | t }}:</v-col>
                <v-col class="col-item" cols="6">
                    <v-text-field
                        :value="activeCompany.contact_email"
                        dense
                        hide-details
                        outlined
                        @input="updateCompanyField($event, 'contact_email')"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col class="subtitle-1" cols="4">{{ "Phone" | t }}:</v-col>
                <v-col class="col-item" cols="6">
                    <v-text-field
                        :value="activeCompany.contact_phone"
                        dense
                        hide-details
                        outlined
                        @input="updateCompanyField($event, 'contact_phone')"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col class="subtitle-1" cols="4">{{ "Address" | t }}:</v-col>
                <v-col class="col-item" cols="6">
                    <v-text-field
                        :value="activeCompany.contact_address"
                        dense
                        hide-details
                        outlined
                        @input="updateCompanyField($event, 'contact_address')"
                    />
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-text v-else>
            <div class="subtitle-1">
                {{ "Email" | t }}:
                {{ activeCompany.contact_email ? activeCompany.contact_email : "-" }}
            </div>
            <div class="subtitle-1">
                {{ "Phone" | t }}:
                {{ activeCompany.contact_phone ? activeCompany.contact_phone : "-" }}
            </div>
            <div class="subtitle-1">
                {{ "Address" | t }}:
                {{ activeCompany.contact_address ? activeCompany.contact_address : "-" }}
            </div>
        </v-card-text>

        <v-card-actions>
            <v-spacer />
            <v-btn color="black" text @click="handleSaveClick">
                {{ "Save" | t }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapActions, mapState } from "vuex";
import Store from "@/store";

export default {
    name: "user-and-company-edit-card",
    computed: {
        ...mapState(["activeUser", "activeCompany", "activeCompanyBanks"])
    },
    data() {
        return {
            changePasswordToggle: false,
            passwordInputType: "password",
            rules: {
                password: value => {
                    const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#%&])(?=.{8,})/;
                    return (
                        pattern.test(value) ||
                        "Min. 8 characters with at least one capital letter, a number and a special character."
                    );
                }
            }
        };
    },
    methods: {
        ...mapActions([
            "submitCompanyUpdate",
            "submitUserUpdate",
            "getUserData",
            "getCompanyData",
            "logOutCurrentUser"
        ]),
        async handleSaveClick() {
             const companyPromise = await this.submitCompanyUpdate({
                company: this.activeCompany,
                banks: this.activeCompanyBanks
            });
            const userPromise = await this.submitUserUpdate(this.activeUser);
            if (userPromise.status === 1 && companyPromise.status === 1) {
                if (userPromise.password_changed) {
                    await Store.commit("setRequestSuccess", true);
                    await setTimeout(async () => {
                        await this.logOutCurrentUser(true);
                    }, 1000);
                }
                await this.$router.push({
                    name: "settings-view"
                });
                await this.getUserData(this.activeUser.id);
                if (this.activeUser.company_id) {
                    await this.getCompanyData(this.activeUser.company_id);
                }
                await Store.commit("setRequestSuccess", true);
                await Store.commit("updateUserFirstnameProperty", this.activeUser.first_name);
            } else {
                console.log(userPromise);
                console.log(companyPromise);
                await Store.commit("setRequestError", true);
                //await Store.commit("setErrorMessage", "Failed");
            }
        },
        updateUserField(value, field) {
            Store.commit("updateActiveUserProperty", { prop: field, value: value });
        },
        updateCompanyField(value, field) {
            Store.commit("updateActiveCompanyProperty", { prop: field, value: value });
        },
        handleClosePasswordClick() {
            this.changePasswordToggle = false;
            Store.commit("updateActiveUserProperty", { prop: "password", value: null });
        }
    }
};
</script>

<style scoped>
.col-item {
    padding-top: 4px;
    padding-bottom: 4px;
}
</style>
